import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import ClosingCTA from "@/components/ClosingCTA";
import SchoolLogo from "@/components/SchoolLogo";
import { useT, useLang } from "@/i18n/LanguageContext";

// School lists kept in English (proper nouns / brand names).
const schoolGroups = [
    { regionKey: "boarding", schools: [
        "Phillips Academy Andover",
        "Phillips Exeter Academy",
        "St. Paul's School",
        "Deerfield Academy",
        "The Lawrenceville School",
        "The Hotchkiss School",
        "Choate Rosemary Hall",
        "Groton School",
        "Middlesex School",
        "Milton Academy",
    ]},
    { regionKey: "nj", schools: [
        "Newark Academy",
        "The Pingry School",
        "Princeton Day School",
        "Gill St. Bernard's",
        "Kent Place School",
        "Montclair Kimberley Academy",
        "Dwight-Englewood School",
        "Saddle River Day School",
    ]},
    { regionKey: "ny", schools: [
        "Trinity School",
        "Horace Mann School",
        "Collegiate School",
        "Dalton School",
        "Brearley School",
        "Spence School",
        "Chapin School",
        "Riverdale Country School",
        "Poly Prep Country Day School",
        "Rye Country Day School",
        "Ethical Culture Fieldston School",
        "Hackley School",
    ]},
    { regionKey: "ca", schools: [
        "Harvard-Westlake School",
        "The Harker School",
        "Castilleja School",
        "Marlborough School",
        "The Bishop's School",
        "Chadwick School",
        "Crystal Springs Uplands School",
        "Menlo School",
        "Head-Royce School",
        "Polytechnic School",
    ]},
];

const REGION_LABELS = {
    en: { boarding: "Top Boarding Schools", nj: "New Jersey", ny: "New York", ca: "California" },
    zh: { boarding: "顶尖寄宿学校",         nj: "新泽西",     ny: "纽约",     ca: "加州" },
};

const stories = {
    en: [
        { name: "E.D.", school: "Phillips Exeter Academy", domain: "exeter.edu", tag: "Boarding",
          body: "A quiet, math-driven 8th grader had excellent test scores but an activity list that looked like many other high-achieving applicants. Instead of adding surface-level activities, we helped the family build the application around his real intellectual spark: topology puzzles, independent problem-solving, and the informal math club he started with friends. The final application positioned him as a young thinker with depth and initiative, not a packaged candidate. Admitted with financial aid." },
        { name: "J.W.", school: "The Pingry School", domain: "pingry.org", tag: "NJ Day School",
          body: "A 5th grader applying for 6th-grade entry had parents who believed she needed more awards to be competitive. Our work helped the family notice what was already distinctive: her love of environmental science, her quiet leadership style, and the way teachers described her empathy and follow-through. The parent essay, student materials, and interview preparation told one consistent story of curiosity and character. Admitted to Pingry and Newark Academy; enrolled at Pingry." },
        { name: "J.C.", school: "Trinity School", domain: "trinityschoolnyc.org", tag: "NY Day School",
          body: "A Kindergarten applicant entered a hyper-competitive Manhattan cycle after being coached elsewhere to rehearse interview answers. We reset the process completely. The goal was not performance; it was readiness, warmth, and authenticity. We helped the parents clarify their values, write from their real parenting experience, and allow their child to show up naturally in the playgroup observation. Admitted in the first round." },
        { name: "S.L.", school: "The Harker School", domain: "harker.org", tag: "CA Day School",
          body: "A 7th grader transferring from public school had no private-school network, no legacy advantage, and parents who spoke limited English. We handled the full application architecture: school selection, ISEE planning, essay development, interview coaching in English and Mandarin, and parent positioning. Her robotics work and Mandarin debate experience became a coherent story of technical curiosity and cross-cultural confidence. Admitted to Harker and Castilleja." },
    ],
    zh: [
        { name: "E.D.", school: "Phillips Exeter Academy", domain: "exeter.edu", tag: "Boarding",
          body: "一个安静、痴迷数学的八年级学生，标化分数优秀，但活动列表与其他高分申请者并无二致。我们没有让家庭去叠加表面的活动，而是把申请围绕他真正的学术火花展开：拓扑学谜题、独立解题，以及他与朋友们自发组建的非正式数学社。最终的申请把他呈现为一个有深度、有主动性的年轻思考者，而非一个被包装出来的候选人。带奖录取。" },
        { name: "J.W.", school: "The Pingry School", domain: "pingry.org", tag: "NJ Day School",
          body: "一个五年级女孩申请六年级入学，父母最初认为她需要更多奖项才有竞争力。我们的工作，是帮家庭看见那些原本就独特的部分：她对环境科学的热爱、她安静的领导力，以及老师们如何描述她的同理心与执行力。家长文书、学生材料与面试辅导讲出了一个关于好奇与品格的连贯故事。最终被 Pingry 与 Newark Academy 同时录取，入读 Pingry。" },
        { name: "J.C.", school: "Trinity School", domain: "trinityschoolnyc.org", tag: "NY Day School",
          body: "曼哈顿超高竞争的幼儿园申请季。家庭此前被其他顾问指导反复演练面试答案。我们把整套流程彻底重启。目标不是表演——而是准备度、温度与真实。我们帮父母厘清自己的价值观，从他们真实的育儿经历中写起，让孩子在小组观察中以最自然的状态出现。第一轮即录取。" },
        { name: "S.L.", school: "The Harker School", domain: "harker.org", tag: "CA Day School",
          body: "一个从公立学校转读私立的七年级学生：没有私校人脉、没有家族背景、父母英文有限。我们承担了完整的申请架构：选校、ISEE 规划、文书、英文与中文双语面试辅导，以及家长定位。她的机器人项目与中文辩论经历，被串成一个关于技术好奇与跨文化自信的连贯故事。最终被 Harker 与 Castilleja 同时录取。" },
    ],
};

export default function K12() {
    const t = useT();
    const { lang } = useLang();
    const p = t.pages.k12;
    const phasesNumbers = ["01", "02", "03"];
    const regionLabels = REGION_LABELS[lang];
    const storyList = stories[lang];
    return (
        <main data-testid="page-k12" className="min-h-screen">
            <Nav />

            <PageHeader
                eyebrow={p.eyebrow}
                title={p.title}
                italicWord={p.italic}
                subtitle={p.subtitle}
                breadcrumb={[{ label: p.crumb }]}
                background="var(--paper)"
            />

            {/* WHAT WE DO */}
            <section className="section-x section-y" style={{ background: "var(--mist)" }}>
                <SectionHeading
                    eyebrow={p.whatWeDo.eyebrow}
                    title={p.whatWeDo.title}
                    subtitle={p.whatWeDo.subtitle}
                    subtitleNowrap
                />

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-8 lg:gap-x-8" style={{ borderTop: "2px solid var(--navy)", paddingTop: 40 }}>
                    {p.phases.map((ph, i) => (
                        <div key={i} data-testid={`k12-phase-${i}`} className="phase-card">
                            <div className="phase-card-body">
                                <div className="flex flex-col items-center text-center">
                                    <div className="phase-circle">
                                        <span
                                            className="font-display"
                                            style={{
                                                fontSize: 24,
                                                lineHeight: 1,
                                                color: "#fff",
                                                fontWeight: 600,
                                                letterSpacing: "-0.01em",
                                            }}
                                        >
                                            {phasesNumbers[i]}
                                        </span>
                                    </div>
                                    <p
                                        className="font-mono mt-5"
                                        style={{
                                            fontSize: 14,
                                            letterSpacing: "0.22em",
                                            textTransform: "uppercase",
                                            color: "var(--navy)",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {ph.label}
                                    </p>
                                    <div className="phase-rule mt-4" />
                                </div>

                                <ul className="mt-6 space-y-3.5">
                                    {ph.items.map((it, j) => (
                                        <li
                                            key={j}
                                            className="flex items-start gap-3"
                                            data-testid={`k12-phase-${i}-item-${j}`}
                                        >
                                            <span
                                                aria-hidden="true"
                                                style={{
                                                    marginTop: 9,
                                                    width: 6,
                                                    height: 6,
                                                    background: "var(--navy)",
                                                    flexShrink: 0,
                                                    borderRadius: "50%",
                                                }}
                                            />
                                            <p
                                                className="font-serif"
                                                style={{ fontSize: 16, lineHeight: 1.55, color: "var(--ink)" }}
                                            >
                                                {it}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* WHERE STUDENTS GO */}
            <section className="section-x section-y" style={{ background: "var(--paper)" }}>
                <SectionHeading
                    eyebrow={p.whereGo.eyebrow}
                    title={p.whereGo.title}
                    subtitle={p.whereGo.subtitle}
                />

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: "var(--line)", border: "1px solid var(--line)" }}>
                    {schoolGroups.map((g, i) => (
                        <article
                            key={g.regionKey}
                            className="p-8 lg:p-10 lift"
                            style={{ background: "var(--mist)" }}
                            data-testid={`k12-region-${i}`}
                        >
                            <div className="flex items-center gap-3">
                                <div style={{ width: 24, height: 1, background: "var(--orange)" }} />
                                <p
                                    className="font-mono"
                                    style={{
                                        fontSize: 11,
                                        letterSpacing: "0.22em",
                                        textTransform: "uppercase",
                                        color: "var(--orange)",
                                        fontWeight: 500,
                                    }}
                                >
                                    {regionLabels[g.regionKey]}
                                </p>
                            </div>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {g.schools.map((s, j) => (
                                    <span
                                        key={s}
                                        data-testid={`k12-region-${i}-school-${j}`}
                                        className="font-display"
                                        style={{
                                            display: "inline-block",
                                            padding: "8px 12px",
                                            background: "var(--paper)",
                                            border: "1px solid var(--line)",
                                            color: "var(--navy)",
                                            fontSize: 13.5,
                                            lineHeight: 1.2,
                                            fontWeight: 500,
                                            letterSpacing: "-0.005em",
                                        }}
                                    >
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* STUDENT STORIES */}
            <section className="section-x section-y" style={{ background: "var(--paper)" }}>
                <SectionHeading
                    eyebrow={p.stories.eyebrow}
                    title={p.stories.title}
                    subtitle={p.stories.subtitle}
                />

                <ol className="mt-12 grid grid-cols-1 gap-px" style={{ background: "var(--line)", border: "1px solid var(--line)" }}>
                    {storyList.map((s, i) => (
                        <li
                            key={s.school + i}
                            className="grid grid-cols-12 gap-x-6 p-8 lg:p-10"
                            style={{ background: "var(--mist)" }}
                            data-testid={`k12-story-${i}`}
                        >
                            <div className="col-span-12 lg:col-span-3">
                                <SchoolLogo domain={s.domain} name={s.school} size={56} imgSize={40} />
                                <p
                                    className="font-mono mt-4"
                                    style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--orange)", fontWeight: 500 }}
                                >
                                    {s.tag}
                                </p>
                                <h3
                                    className="font-display mt-2"
                                    style={{ fontSize: 20, color: "var(--navy)", fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.005em" }}
                                >
                                    {s.school}
                                </h3>
                                <p
                                    className="font-mono mt-3"
                                    style={{ fontSize: 11, letterSpacing: "0.18em", color: "var(--steel)", textTransform: "uppercase" }}
                                >
                                    {s.name}
                                </p>
                            </div>
                            <div className="col-span-12 lg:col-span-9 mt-3 lg:mt-0">
                                <p className="font-serif" style={{ fontSize: 16.5, lineHeight: 1.65, color: "var(--ink)" }}>
                                    {s.body}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>

            <ClosingCTA />
            <Footer />
        </main>
    );
}
